import { z } from 'zod';
import { AppListing, AppMedia } from './apps.js';
import { Platform, Ulid } from './common.js';
import { StoreVerificationMode } from './store.js';
/**
 * `GET /v1/me/library` — the device's whole world in one round trip (plan §5, §8).
 *
 * Deliberately one call with an ETag: the Board library screen renders from a single
 * response, and a `304` on a warm start costs nothing. Version history is *not* here — a
 * tester only needs it when they open an app, and including it would bloat the payload for
 * every app they never touch.
 */
/**
 * How the caller came to see this app. Testers never learn they got it via a team.
 *
 * `public` is the store's (`docs/store-plan.md` §2): the game is published and this account
 * pressed Get, which is the whole of what entitles them. It ranks *below* `code` wherever a
 * strongest source is picked — a redeem code somebody handed you says more about your access
 * than a shelf everyone can see does.
 *
 * `review` is an operator's, while a game waits for them (`docs/store-review-plan.md`): exactly the
 * `public` channel, for as long as the submission is pending and not a moment after. It ranks
 * lowest of all — it is not access anybody gave, and it ends.
 *
 * The Android client's `entitlement` field is a lenient `String`, so a client built before either
 * word existed parses a library carrying it.
 */
export const EntitlementSource = z.enum(['team', 'grant', 'code', 'public', 'review']);
export const LibraryBuild = z.object({
    id: Ulid,
    buildNumber: z.number().int().nullable(),
    versionName: z.string(),
    versionCode: z.number().int(),
    size: z.number().int(),
    /** The device re-verifies this after download, before handing bytes to Board. */
    sha256: z.string(),
    releaseNotes: z.string().nullable(),
    uploadedBy: z.string().nullable(),
    /** Set when this build was installed and launched on a real Board (plan §15). */
    verifiedAt: z.number().int().nullable().optional(),
    /**
     * The certificate these bytes are signed with, when BoardFlight signed them
     * (docs/feedback-plan.md §3.4). Null for a build carrying the developer's own signature,
     * which is every build of a game that has never turned the in-game feedback layer on.
     *
     * The device compares it against the certificate of the build it last installed for this
     * package, and warns *before* a several-hundred-megabyte download that Android is going to
     * refuse. `.optional()` because an older client parses a library it has never seen this
     * field in and must not fail on it.
     */
    signingCertSha256: z.string().nullable().optional(),
    /**
     * The Board Piece Model this build declared, as a registry stem (`chop_chop`), or null when
     * nothing was declared (`docs/model-intro-plan.md`).
     *
     * `.optional()` for the same reason `signingCertSha256` is: an older client must parse a library
     * that carries it, and an older server's response must parse in a newer client. The client
     * resolves it through its own copy of the registry and draws an unknown stem by name — a
     * developer's private model and a model newer than the client are the same case.
     */
    pieceModel: z.string().nullable().optional(),
    createdAt: z.number().int(),
});
export const LibraryChannel = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    /**
     * Whether to show the Board Piece Model interstitial before launching from this channel.
     *
     * Unlike `Channel.enabled` — which is absent here because the library only carries channels
     * that are on, so it would be a constant — this one genuinely varies per channel and the device
     * is the only thing that reads it. `.optional()` on the wire so an older server's library
     * parses; absent is off, which is the state everything but `public` starts in.
     */
    modelIntro: z.boolean().optional(),
    /**
     * Which of the game's models this Board installs here — `top`, `auto` or `pick` (P4,
     * `MODEL_CHOICES`). A string rather than the enum, so a value from a newer server parses; the
     * device reads anything it does not know as `top`. Absent — a server older than the client, which the
     * deploy order never produces — reads as `auto`.
     */
    modelChoice: z.string().optional(),
    /**
     * The Board Piece Model this channel is about by default (migration 0038).
     *
     * `.optional()` on the wire for the same reason `modelIntro` is: an older server sends no such
     * field and the client must still parse the response. Absent is `arcade`, which is what 0038
     * backfilled every channel to, so an old server and a new client agree by construction.
     */
    primaryModel: z.string().optional(),
    /**
     * Every model this game offers here — the menu the install resolves against.
     *
     * A client intersects this with the sets the player owns. It is deliberately the channel's list
     * and not the set of models that happen to have builds: a listed model with no build yet is
     * something a store page may say out loud, and `modelBuilds` is where the client finds out which
     * of them it can actually install today.
     */
    supportedModels: z.array(z.string()).optional(),
    /**
     * The current build **per supported model** — the sibling set, filtered to the menu.
     *
     * Unlike the developer-facing `Channel.modelBuilds`, an orphaned sibling — a build for a model
     * the team has since unlisted — is **not** here. A developer should see one; an installer must
     * never offer one.
     */
    modelBuilds: z.array(LibraryBuild).optional(),
    /** Null when the channel exists but has no ready build yet. */
    latestBuild: LibraryBuild.nullable(),
});
export const LibraryApp = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    platform: Platform,
    /** Cross-referenced on-device against Board's `GET /v1/apps` to compute the status chip. */
    packageName: z.string(),
    description: z.string().nullable(),
    iconUrl: z.string().nullable(),
    /**
     * The store page, shipped with the library rather than fetched per app.
     *
     * Version history is excluded above because it is a per-app cost paid for apps nobody
     * opens; the listing is the opposite — it is what the library screen and the detail screen
     * are *made of*, and fetching it separately would mean a tester watching an empty page
     * fill in. It is a few hundred bytes of text and URLs; the bytes behind those URLs are
     * fetched only when something is actually displayed.
     */
    listing: AppListing,
    media: z.array(AppMedia),
    entitlement: EntitlementSource,
    /** Only the channels this caller is entitled to; a tester usually has exactly one. */
    channels: z.array(LibraryChannel),
});
/**
 * `GET /v1/store/apps/:appId` — one game's whole store page (`docs/store-plan.md` §3.5).
 *
 * Here rather than in `store.ts` because it is a `LibraryApp` with facts wrapped around it, and
 * that is the load-bearing part: the store page, the redeem preview and a tester's library are
 * the *same* serialization, so a detail screen renders whichever world opened it without a
 * second shape to keep in step. `store.ts` is imported by `apps.ts`, which this file imports, so
 * the type has to sit on this side of that edge.
 */
export const StoreAppDetail = z.object({
    app: LibraryApp,
    teamName: z.string(),
    /** Rounded, like a card's — this page is read by strangers too. */
    plays: z.number().int(),
    installs: z.number().int(),
    /** Never claim "verified on Board hardware" when this is `none` (§3.4). */
    verification: StoreVerificationMode,
    /**
     * Whether the caller already has it. `false` for an anonymous reader, which is the honest
     * answer: there is no account for it to be true of, and the button says "Get" either way.
     */
    added: z.boolean(),
});
export const LibraryTeam = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    apps: z.array(LibraryApp),
});
/**
 * How to hear about a build without asking — see `apps/api/src/lib/realtime.ts`.
 *
 * Carried here rather than on its own endpoint because the device already makes this call and
 * the answer changes about once a year; a second round trip on every cold start to learn a URL
 * would be a round trip for nothing.
 *
 * `key` is public by design. It identifies the app to the socket service and authorises nothing:
 * every channel a device subscribes to is private, and the signature that opens one comes from
 * `POST /v1/me/realtime/auth` after the same entitlement check this response was built from.
 *
 * Null on a deployment with no realtime credentials, which is supported — the device polls
 * regardless, and the only difference is whether a notice takes a second or a minute.
 */
export const RealtimeConfig = z.object({
    key: z.string(),
    wsUrl: z.string(),
});
export const Library = z.object({
    generatedAt: z.number().int(),
    teams: z.array(LibraryTeam),
    /** `.optional()` so a client built before this field parses a response carrying it. */
    realtime: RealtimeConfig.nullable().optional(),
});
/**
 * `POST /v1/me/realtime/auth` — signatures for the channels a device wants to subscribe to.
 *
 * Batched, unlike Pusher's own one-channel-per-request endpoint. A tester with a dozen games is
 * entitled to a dozen channels, and a round trip each on every reconnect is a lot of requests to
 * answer one question the server can answer once. Both ends here are ours; the socket service
 * only ever sees the finished signature on a subscribe frame and does not care how it was got.
 */
export const RealtimeAuthRequest = z.object({
    socketId: z.string().min(1).max(128),
    channelNames: z.array(z.string().min(1).max(128)).min(1).max(200),
});
export const RealtimeAuthResponse = z.object({
    /**
     * Channel name to signature, containing **only** the channels the caller may actually have.
     * A channel they are not entitled to is absent rather than an error: entitlements change
     * between a device's last library read and this call, and one stale name in a batch of twelve
     * must not cost the tester the other eleven.
     */
    auth: z.record(z.string(), z.string()),
});
/**
 * The most games one account keeps starred in My Games. A row of quick-launch tiles, not a second
 * library: past this many the row is no quicker than the list under it.
 */
export const MAX_FAVORITES = 24;
/**
 * `GET /v1/me/favorites`, and the answer to `PUT`/`DELETE /v1/me/favorites/:appId` — the games this
 * account starred, oldest star first.
 *
 * Only ids, and only of games still in the account's library: everything drawn about a game comes
 * from `GET /v1/me/library`, and a game the account lost access to is left out rather than named.
 * A person's own shelf, never counted and never shown to anybody else.
 */
export const Favorites = z.object({
    appIds: z.array(Ulid),
});
//# sourceMappingURL=library.js.map